import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useLocation} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {MessagingProviders} from '@adv-mt/mt-messaging'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {PUBLIC_KEY_GETSTREAM, ROUTES} from '../../../config/index.js'
import InfoArea from './InfoArea/InfoArea.js'
import {useMessagingContext} from './useMessagingContext/index.js'
import MessagingThirdParty from './messagingThirdParty.js'

const WIDGET_ID = 'mt-Messaging-Migration'

const {FullWidth: LayoutFullWidth, FullHeight: LayoutFullHeight} = Layout

const UserMessagesPage = ({authToken, pageTrackingData, userData}) => {
  const {USER} = ROUTES
  const {domain} = useContext(Context)
  const {pathname, state: locationState} = useLocation()
  const hasDirectConversation = Boolean(locationState?.adId)

  const handleFirstMessage = async ({message, adId}) => {
    const [error] = await domain.get('contact_by_mail_use_case').execute({
      adId: adId || locationState?.adId,
      message
    })
    if (error) throw error
  }
  const [adsList, setAdsList] = useState({})
  const {state, setAdsDetails: setAdsDetailsInContext} = useMessagingContext()
  const {adsDetails} = state

  const [tokenProvider, setTokenProvider] = useState({data: null, loading: true, error: false})

  const isMobileOrTablet = !useMediaQuery(useMediaQueryBreakpoints.lg)

  const {page: pageTrack} = useAnalyticsTrack()

  const getAdDetailResponse = async ({adId}) => {
    const [error, adsDetailsResponse] = await domain.get('get_ad_details_ads_use_case').execute({adId})

    if (!error) {
      setAdsDetailsInContext(prev => ({...prev, [adsDetailsResponse.ad.id]: adsDetailsResponse}))
    }
  }

  const getCurrentStreamToken = async () => {
    const [tokenError, token] = await domain.get('get_mesasging_provider_token_user_use_case').execute()
    if (tokenError) {
      setTokenProvider({data: null, loading: false, error: true})
      return
    }
    setTokenProvider({data: token.messagingProviderToken, loading: false, error: false})
  }

  //* **** STREAM CONFIG ******//
  const clientData = {
    apiKey: PUBLIC_KEY_GETSTREAM,
    tokenOrProvider: tokenProvider.data,
    userId: userData?.userId || ''
  }

  //* **** END OF STREAM CONFIG ******//

  const updateAdsList = newAds => {
    setAdsList(prev => ({...prev, ...newAds}))
  }

  const uploadAttachment = async (channelId, file) => {
    const [error, response] = await domain.get('upload_attachment_user_use_case').execute({channelId, file})

    if (error) throw error
    return response
  }

  const getMessagingChannelContext = async channelId => {
    const [error, response] = await domain.get('get_messaging_channel_context_user_use_case').execute({channelId})

    if (error) console.error(error)
    return response || error
  }

  useMount(() => {
    pageTrack(pageTrackingData)
    getCurrentStreamToken()
  })

  const Layout = isMobileOrTablet ? LayoutFullWidth : LayoutFullHeight

  // WIP: manage provider token errors
  return (
    <NavigationRequireAuth currentPath={pathname}>
      <Layout linkFactory={LinkFactory} isFooterless isPaddingless isMarginless>
        <main className="mt-UserMessages">
          <section className="mt-UserMessages-content">
            <div className="mt-UserMessages-widget" id={WIDGET_ID}>
              {Boolean(userData.userId) && tokenProvider !== null && (
                <MessagingProviders
                  adsDetails={adsDetails}
                  adsList={adsList}
                  currentUser={{name: userData.name, id: userData.userId, email: userData.email}}
                  clientData={clientData}
                  hideChannelList={false}
                  avoidRedirectToFirstContact={hasDirectConversation}
                  channelHeaderElement={InfoArea}
                  urlBasePath={USER.MESSAGES}
                  uploadAttachment={uploadAttachment}
                  getMessagingChannelContext={getMessagingChannelContext}
                  onFirstMessage={hasDirectConversation ? handleFirstMessage : undefined}
                  defaultMessage={locationState?.defaultMessage}
                  initialAdId={locationState?.adId}
                >
                  <MessagingThirdParty
                    adsList={adsList}
                    adsDetails={adsDetails}
                    getAdDetailResponse={getAdDetailResponse}
                    updateAdsList={updateAdsList}
                  />
                </MessagingProviders>
              )}
            </div>
          </section>
        </main>
      </Layout>
    </NavigationRequireAuth>
  )
}

UserMessagesPage.displayName = 'UserMessagesPage'

UserMessagesPage.propTypes = {
  authToken: PropTypes.string,
  pageTrackingData: PropTypes.object,
  userData: PropTypes.object
}

export default UserMessagesPage
